import { bytesToNDigits } from '@mail/cross-sizes-utils';
import { priorityDownload } from 'reactApp/appHelpers/featuresHelpers/features/priorityDownload';
import { getWeblinkFromPublicId } from 'reactApp/modules/file/utils';
import { DownloadPriorityActions } from 'reactApp/modules/priorityDownload/priorityDownload.module';
import type { PriorityDownloadDwhArgs } from 'reactApp/modules/priorityDownload/priorityDownload.types';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { isCloudFile } from 'reactApp/modules/storage/storage.helpers';
import type { CloudItem } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { store as reduxStore } from 'reactApp/store';
import { select } from 'typed-redux-saga';

export const processDownloadPriority = (item: CloudItem | CloudItem[]) => {
    if (priorityDownload.enabled) {
        return new Promise((resolve) => {
            reduxStore.dispatch(DownloadPriorityActions.checkDownloadPriority({ item, continueDownload: () => resolve(true) }));
        });
    }
    return Promise.resolve();
};

export function checkItemsPriority(item: CloudItem | CloudItem[]) {
    const items = [item].flat();
    return items.length > priorityDownload.filesInPackLimit || items.some(checkItemPriority);
}

function checkItemPriority(item: CloudItem) {
    if (isCloudFile(item)) {
        return item.size >= priorityDownload.fileSizeLimit.original;
    }

    return item.isFolder;
}

function getItemExtensions(item: CloudItem | CloudItem[]) {
    const items = [item].flat();
    return items.filter(isCloudFile).map((item) => {
        return item.ext;
    });
}

function getItemWeblinkId(item: CloudItem | CloudItem[]) {
    const firstItem = [item].flat()[0];
    if (firstItem?.weblink) {
        return getWeblinkFromPublicId(firstItem.weblink);
    }
    return;
}

function getTypeDownloads(item: CloudItem | CloudItem[]) {
    if (Array.isArray(item)) {
        if (item.length === 1) {
            return getTypeDownloads(item[0]);
        }
        return 2;
    }
    return isCloudFile(item) ? 3 : 1;
}

function getFilesAndFoldersCount(item: CloudItem | CloudItem[]) {
    const items = [item].flat();
    const filesCount = items.filter(isCloudFile).length;
    return { filesCount, foldersCount: items.length - filesCount };
}

function getFilesSize(item: CloudItem | CloudItem[]) {
    const items = [item].flat();
    return items.filter(isCloudFile).map((item) => bytesToNDigits(item.size, 3));
}

export function* getPriorityItemDwh(item: CloudItem | CloudItem[]) {
    const isPaidUser = yield* select(UserSelectors.isPaidUser);
    const storage = yield* select(getCurrentStorage);
    const { total, used } = yield* select(UserSelectors.getCloudSpace);
    const { filesCount, foldersCount } = getFilesAndFoldersCount(item);
    const dwh: PriorityDownloadDwhArgs = {
        paid_user: isPaidUser,
        extensions: getItemExtensions(item),
        public_id: getItemWeblinkId(item),
        type_downloads: getTypeDownloads(item),
        count_files: filesCount,
        count_folders: foldersCount,
        size_files: getFilesSize(item).map((s) => s.original),
        size_files_values: getFilesSize(item).map((s) => s.value),
        source: storage,
        quota: used?.original,
        quota_value: used?.value,
        total_quota: total?.original,
        total_quota_value: total?.value,
    };
    return dwh;
}
